/**
 * HTTP Client Configuration Utilities
 * 
 * Provides shared HTTP client configuration for Amaster backend integration
 */

import { type HttpClientOptions } from "@amaster.ai/http-client";

/**
 * Transform Amaster backend response format
 * Backend returns: { statusCode: 200, data: {...}, message: "..." }
 * We extract the inner 'data' field for cleaner client usage
 * 
 * @param responseData - Raw response data from backend
 * @returns Extracted data or original response if not in Amaster format
 * 
 * @example
 * ```typescript
 * const response = { statusCode: 200, data: { userId: "123" }, message: "Success" };
 * const result = transformAmasterResponse(response);
 * // result = { userId: "123" }
 * ```
 */
export function transformAmasterResponse<T>(responseData: unknown): T {
  if (
    responseData &&
    typeof responseData === "object" &&
    ("statusCode" in responseData || "status" in responseData)
  ) {
    // If response has 'data' field, extract it
    if ("data" in responseData) {
      return (responseData as { data: T }).data;
    }
    // Otherwise return the whole response (e.g., for SuccessResponse without data)
    return responseData as T;
  }
  return responseData as T;
}

/**
 * Default HTTP client options for Amaster backend
 * Includes response transformation and error logging
 */
export const defaultHttpClientOptions: HttpClientOptions = {
  transformResponse: transformAmasterResponse,
  // Enable error logging by default (helps with debugging and AI assistance)
  logErrors: true,
};
